/**
 * Interval computation for multi-country bulk scheduling.
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { applyScheduleInterval } from '@/features/smswiser-campaigns/utils/scheduleInterval';

import type { IntervalMode, PerItemScheduleEntry } from './multiCountryBulkEdit.types';

export interface IntervalInput {
  ids: string[];
  mergedDrops: PreparedDrop[];
  baseUtcIso: string;
  intervalMinutes: number;
  mode: IntervalMode;
}

function resolveDrops(ids: string[], mergedDrops: PreparedDrop[]): PreparedDrop[] {
  const byId = new Map(mergedDrops.map((d) => [d.id, d]));
  return ids.map((id) => byId.get(id)).filter((d): d is PreparedDrop => Boolean(d));
}

export function computeOneSequenceIntervals(params: {
  drops: PreparedDrop[];
  baseUtcIso: string;
  intervalMinutes: number;
}): PerItemScheduleEntry[] {
  const { drops, baseUtcIso, intervalMinutes } = params;
  return drops.map((d, i) => ({
    countryKey: d.countryKey,
    id: d.id,
    wiserId: d.wiserId,
    payload: {
      executeAfter: applyScheduleInterval(baseUtcIso, intervalMinutes, i),
    },
  }));
}

export function computePerCountryIntervals(params: {
  drops: PreparedDrop[];
  baseUtcIso: string;
  intervalMinutes: number;
}): PerItemScheduleEntry[] {
  const { drops, baseUtcIso, intervalMinutes } = params;
  const countryIndex = new Map<string, number>();
  return drops.map((d) => {
    const idx = countryIndex.get(d.countryKey) ?? 0;
    countryIndex.set(d.countryKey, idx + 1);
    return {
      countryKey: d.countryKey,
      id: d.id,
      wiserId: d.wiserId,
      payload: {
        executeAfter: applyScheduleInterval(baseUtcIso, intervalMinutes, idx),
      },
    };
  });
}

export function computeMultiCountryIntervals(input: IntervalInput): PerItemScheduleEntry[] {
  const drops = resolveDrops(input.ids, input.mergedDrops);
  if (drops.length === 0) return [];

  const common = {
    drops,
    baseUtcIso: input.baseUtcIso,
    intervalMinutes: input.intervalMinutes,
  };

  if (input.mode === 'one_sequence') {
    return computeOneSequenceIntervals(common);
  }
  return computePerCountryIntervals(common);
}
